import { Link, NavLink as RouterNavLink } from 'react-router-dom';
import { Menu, ArrowRight } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { Logo } from '@/components/common/Logo';
import { PRIMARY_NAV } from '@/constants/site';
import { SERVICE_CATEGORIES } from '@vegaahi/shared';
import { getServicesByCategory } from '@/constants/services.data';
import { cn } from '@/utils/cn';

function ServicesMegaMenu() {
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-6 p-6 md:w-[820px] md:grid-cols-4">
      {SERVICE_CATEGORIES.map((category) => (
        <div key={category.slug}>
          <p className="text-muted-foreground mb-3 text-xs font-semibold uppercase tracking-wide">
            {category.name}
          </p>
          <ul className="space-y-2">
            {getServicesByCategory(category.slug).map((service) => (
              <li key={service.slug}>
                <NavigationMenuLink asChild>
                  <Link
                    to={`/services/${service.slug}`}
                    className="hover:text-primary text-foreground/80 flex items-start gap-2 text-sm transition-colors"
                  >
                    <service.icon
                      className="text-primary mt-0.5 h-4 w-4 shrink-0"
                      aria-hidden="true"
                    />
                    <span>{service.name}</span>
                  </Link>
                </NavigationMenuLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <div className="border-border col-span-2 flex items-center justify-between border-t pt-4 md:col-span-4">
        <p className="text-muted-foreground text-sm">27+ specialisms, one delivery team.</p>
        <NavigationMenuLink asChild>
          <Link
            to="/services"
            className="text-primary inline-flex items-center gap-1 text-sm font-semibold"
          >
            View all services <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </NavigationMenuLink>
      </div>
    </div>
  );
}

function DesktopNav() {
  return (
    <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger>Services</NavigationMenuTrigger>
          <NavigationMenuContent>
            <ServicesMegaMenu />
          </NavigationMenuContent>
        </NavigationMenuItem>
        {PRIMARY_NAV.filter((link) => link.href !== '/services').map((link) => (
          <NavigationMenuItem key={link.href}>
            <NavigationMenuLink asChild>
              <RouterNavLink
                to={link.href}
                className={({ isActive }) =>
                  cn(
                    'hover:bg-muted inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors',
                    isActive && 'text-primary',
                  )
                }
              >
                {link.label}
              </RouterNavLink>
            </NavigationMenuLink>
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
}

function MobileNav() {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu">
          <Menu />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full max-w-sm flex-col">
        <SheetHeader>
          <SheetTitle>
            <Logo />
          </SheetTitle>
        </SheetHeader>
        <nav className="mt-6 flex flex-col gap-1">
          {PRIMARY_NAV.map((link) => (
            <SheetClose asChild key={link.href}>
              <RouterNavLink
                to={link.href}
                className={({ isActive }) =>
                  cn(
                    'hover:bg-muted rounded-lg px-3 py-3 text-base font-medium transition-colors',
                    isActive && 'text-primary bg-muted',
                  )
                }
              >
                {link.label}
              </RouterNavLink>
            </SheetClose>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-3 pt-6">
          <SheetClose asChild>
            <Button asChild size="lg" className="w-full">
              <Link to="/contact">
                Get in touch <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function Header() {
  return (
    <header className="bg-background/80 border-border sticky top-0 z-50 border-b backdrop-blur-lg">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
        <Logo />
        <DesktopNav />
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button asChild size="default" className="hidden lg:inline-flex">
            <Link to="/contact">
              Get in touch <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <MobileNav />
        </div>
      </div>
    </header>
  );
}
